'use client';

import { Button } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { AlertCircleIcon, WifiDisconnected01Icon } from '@terra/ui-icons';
import {
  CHAT_LOAD_ERROR_KIND,
  resolveChatLoadErrorKind,
} from '../../utils/chatNetworkErrors';
import { EmptyState } from '../EmptyState';

export const CHAT_HISTORY_LOAD_STATUS = {
  loading: 'loading',
  error: 'error',
} as const;
export type ChatHistoryLoadStatus =
  | (typeof CHAT_HISTORY_LOAD_STATUS)[keyof typeof CHAT_HISTORY_LOAD_STATUS]
  | null;
type ChatHistoryLoadStateProps = {
  status: ChatHistoryLoadStatus;
  historyLoadError: Error | null;
  onRetry: () => void;
};

export function resolveChatHistoryLoadStatus({
  isExistingConversation,
  isLoadingHistory,
  historyLoadError,
  keepChatVisibleDuringHistoryLoad = false,
}: {
  isExistingConversation: boolean;
  isLoadingHistory: boolean;
  historyLoadError: Error | null;
  keepChatVisibleDuringHistoryLoad?: boolean;
}): ChatHistoryLoadStatus {
  if (!isExistingConversation) {
    return null;
  }

  if (isLoadingHistory && !keepChatVisibleDuringHistoryLoad) {
    return CHAT_HISTORY_LOAD_STATUS.loading;
  }

  if (historyLoadError) {
    return CHAT_HISTORY_LOAD_STATUS.error;
  }

  return null;
}

export function ChatHistoryLoadState({
  status,
  historyLoadError,
  onRetry,
}: ChatHistoryLoadStateProps) {
  const tCopilot = useTranslations('chat.copilot');
  const tCommon = useTranslations('common');

  if (!status) {
    return null;
  }

  if (status === CHAT_HISTORY_LOAD_STATUS.loading) {
    return (
      <EmptyState
        icon={AlertCircleIcon}
        title={tCopilot('historyLoad.loadingTitle')}
        isLoading
      />
    );
  }

  if (!historyLoadError) {
    return null;
  }

  const errorKind = resolveChatLoadErrorKind(historyLoadError);
  const errorIcon =
    errorKind === CHAT_LOAD_ERROR_KIND.connectivity
      ? WifiDisconnected01Icon
      : AlertCircleIcon;
  const errorTitle =
    errorKind === CHAT_LOAD_ERROR_KIND.connectivity
      ? tCopilot('historyLoad.connectivityErrorTitle')
      : errorKind === CHAT_LOAD_ERROR_KIND.server
        ? tCopilot('historyLoad.serverErrorTitle')
        : tCopilot('historyLoad.errorTitle');
  const errorDescription =
    errorKind === CHAT_LOAD_ERROR_KIND.connectivity
      ? tCopilot('historyLoad.connectivityErrorDescription')
      : errorKind === CHAT_LOAD_ERROR_KIND.server
        ? tCopilot('historyLoad.serverErrorDescription')
        : tCopilot('historyLoad.errorDescription');

  return (
    <EmptyState
      icon={errorIcon}
      title={errorTitle}
      description={errorDescription}
      action={
        <Button size="sm" onClick={onRetry}>
          {tCommon('retry')}
        </Button>
      }
    />
  );
}
